import React, { useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { buildURI } from 'react-csv/lib/core';

const isValidUrl = urlString => {
  try {
    return Boolean(new URL(urlString));
  } catch (error) {
    console.error(error);
    return false;
  }
};

const buildCsvMatrix = data => {
  let urlReport = false;
  const exportCSVData =
    data?.length > 1
      ? data.slice(1).map(val =>
          val?.map(arrVal => {
            if (isValidUrl(arrVal)) {
              urlReport = true;
              return `=HYPERLINK(""${arrVal}"")`;
            }
            return arrVal;
          })
        )
      : [];
  const csvData = data?.length > 0 ? [data[0], ...exportCSVData] : [];
  return { csvData, urlReport };
};

const downloadMatrixAsCsv = (matrix, baseFilename) => {
  if (!Array.isArray(matrix) || matrix.length === 0) {
    return;
  }
  let href;
  try {
    href = buildURI(matrix, true, undefined, ',', '"');
  } catch (e) {
    console.error('CSV export failed', e);
    return;
  }
  const safeBase = String(baseFilename || 'export').replace(
    /[/\\?%*:|"<>]/g,
    '_'
  );
  const downloadName = safeBase.toLowerCase().endsWith('.csv')
    ? safeBase
    : `${safeBase}.csv`;
  const link = document.createElement('a');
  link.href = href;
  link.download = downloadName;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (typeof href === 'string' && href.startsWith('blob:')) {
    window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
  }
};

const ExportWidgetTableData = ({
  exportData,
  groupExportData = [],
  widget,
  groupByHandler,
  filename = 'export',
  groupFilename = 'groups_export',
}) => {
  const exportOverlayRef = useRef(null);
  const groupExportOverlayRef = useRef(null);

  const hideExportOverlay = () => {
    exportOverlayRef?.current?.hide();
  };

  const hideGroupExportOverlay = () => {
    groupExportOverlayRef?.current?.hide();
  };

  const exportCsv = () => {
    const { csvData } = buildCsvMatrix(exportData);
    downloadMatrixAsCsv(csvData, filename);
    hideExportOverlay();
  };

  const exportPdf = () => {
    const { urlReport } = buildCsvMatrix(exportData);
    import('jspdf').then(jsPDF => {
      import('jspdf-autotable').then(() => {
        if (Array.isArray(exportData) && exportData?.length > 0) {
          const doc = new jsPDF.default('l', 'pt');
          const exportColumns = exportData[0];
          const pdfData = exportData?.slice(1);
          doc.autoTable(exportColumns, pdfData, {
            styles: { fontSize: 6, cellWidth: urlReport && 58 },
            willDrawCell: data => {
              if (isValidUrl(data?.cell?.raw)) {
                doc.link(data.cell.x, data.cell.y, 20, 20, {
                  url: data.cell.text.join(''),
                });
                data.cell.text = 'Click Here';
              }
            },
          });
          doc.save(filename);
        }
      });
    });
    hideExportOverlay();
  };

  const exportGroupCsv = () => {
    const { csvData } = buildCsvMatrix(groupExportData);
    downloadMatrixAsCsv(csvData, groupFilename);
    hideGroupExportOverlay();
  };

  const exportGroupPdf = () => {
    const { urlReport: groupUrlReport } = buildCsvMatrix(groupExportData);
    import('jspdf').then(jsPDF => {
      import('jspdf-autotable').then(() => {
        if (Array.isArray(groupExportData) && groupExportData?.length > 0) {
          const doc = new jsPDF.default('l', 'pt');
          const exportColumns = groupExportData[0];
          const pdfData = groupExportData?.slice(1);
          doc.autoTable(exportColumns, pdfData, {
            styles: { fontSize: 6, cellWidth: groupUrlReport && 58 },
            willDrawCell: data => {
              if (isValidUrl(data?.cell?.raw)) {
                doc.link(data.cell.x, data.cell.y, 20, 20, {
                  url: data.cell.text.join(''),
                });
                data.cell.text = 'Click Here';
              }
            },
          });
          doc.save(groupFilename);
        }
      });
    });
    hideGroupExportOverlay();
  };

  return (
    <div className="flex gap-2">
      {widget?.backToGroupBy && (
        <Button
          size="small"
          severity="primary"
          variant="contained"
          onClick={groupByHandler}
        >
          Cancel Preview
        </Button>
      )}
      {widget?.report_group_by ? (
        <>
          <Button
            severity="primary"
            size="small"
            variant="contained"
            onClick={groupByHandler}
          >
            Export Preview
          </Button>
          <Button
            severity="primary"
            variant="contained"
            outlined
            size="small"
            className="p-2"
            onClick={e => groupExportOverlayRef?.current?.toggle(e)}
          >
            Export Groups
          </Button>
          <OverlayPanel ref={groupExportOverlayRef}>
            <div className="flex flex-column gap-2">
              <Button
                size="small"
                severity="primary"
                outlined
                onClick={exportGroupCsv}
                disabled={!groupExportData?.length}
              >
                Export in CSV
              </Button>
              <Button
                size="small"
                severity="primary"
                outlined
                onClick={exportGroupPdf}
              >
                Export in PDF
              </Button>
            </div>
          </OverlayPanel>
        </>
      ) : (
        <>
          <Button
            severity="primary"
            variant="contained"
            outlined
            size="small"
            className="p-2"
            onClick={e => exportOverlayRef?.current?.toggle(e)}
          >
            Export
          </Button>
          <OverlayPanel ref={exportOverlayRef}>
            <div className="flex flex-column gap-2">
              <Button
                size="small"
                severity="primary"
                outlined
                onClick={exportCsv}
                disabled={!exportData?.length}
              >
                Export in CSV
              </Button>
              <Button
                size="small"
                severity="primary"
                outlined
                onClick={exportPdf}
              >
                Export in PDF
              </Button>
            </div>
          </OverlayPanel>
        </>
      )}
    </div>
  );
};

export default ExportWidgetTableData;
